import axios from "axios";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import React, { useEffect, useState } from "react";
import { CircularProgress, Dialog, DialogContent, DialogTitle } from "@mui/material";

import FormGenerator from "../form/FormGenerator";
import { Transition } from "../../Modal/ModalConstants";
import { FormFieldType, FormFieldTypesAllowed } from "../../types/form/form-types";
import { selectedCustomersIdsSelector } from "../../features/authentication/auth-slice";
import { APP_BASE_URL, GET_ASSETS, SUB_DEVICES_PATH, trimAssetName } from "../../Common/EndPoints";

const ModalTransition = Transition as any;

const ModalWrapper: React.FC<any> = (props) => {
  const { open, onClose } = props;
  return (
    <div>
      <Dialog
        open={open}
        TransitionComponent={ModalTransition}
        keepMounted
        onClose={onClose}
        aria-describedby="alert-dialog-slide-description"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Add Connections to this Device</DialogTitle>
        <DialogContent>
          <ConnectedDevicesWrapper {...props} />
        </DialogContent>
      </Dialog>
    </div>
  );
};
const ConnectedDevicesWrapper: React.FC<any> = (props) => {
  const { device, customerIds } = props;
  const [state, setState] = useState({
    connected_devices: [],
    loading: true,
    device_type_list: [],
    device_list_loading: true,
    device_list: [],
    all_device_type_list: [],
    device_type_to_device_id_map: {},
  });
  const selected_customer_ids = useSelector(selectedCustomersIdsSelector);

  async function getConnectedDeviceList() {
    try {
      const response = await axios.get(APP_BASE_URL + SUB_DEVICES_PATH, {
        params: { deviceId: device.deviceId },
      });
      return response.data.data;
    } catch (error) {
      throw error;
    }
  }

  async function getDevicesInfo(params: any) {
    try {
      const device_type_list: any = new Set();
      const device_list: any = [];
      const response = await axios.get(APP_BASE_URL + GET_ASSETS, { params });

      for (const device of response.data.data) {
        device_list.push({
          id: device.deviceid,
          label: trimAssetName(device.name),
        });
        device_type_list.add(device?.deviceType?.label);
      }

      return { connected_devices: device_list, device_type_list };
    } catch (error) {
      throw error;
    }
  }
  async function getConnectedDeviceListWithInfo() {
    try {
      const devices_list = await getConnectedDeviceList();
      if (devices_list.length === 0) {
        setState((state) => ({ ...state, connected_devices: [], loading: false }));
        return;
      }
      const { connected_devices, device_type_list } = await getDevicesInfo({
        deviceIds: devices_list.toString(),
        with_type: true,
      });
      setState((state) => ({
        ...state,
        connected_devices,
        loading: false,
        device_type_list: Array.from(device_type_list),
      }));
    } catch (error) {
      console.error("FETCH_CONNECTED_DEVICES_WITH_INFO", error);
      setState((state) => ({ ...state, loading: false }));
    }
  }

  useEffect(() => {
    getDevicesList();
  }, [selected_customer_ids, customerIds]);

  async function getDevicesList() {
    setState((state: any) => ({ ...state, device_list_loading: true }));
    const params = {
      customerIds: customerIds?.length === 0 ? selected_customer_ids.toString() : customerIds.toString(),
      with_type: true,
    };
    const response_raw = await axios.get(APP_BASE_URL + GET_ASSETS, {
      params,
    });
    const response = response_raw.data.data;
    const device_type_set: Set<string> = new Set();
    const device_type_to_device_id_map: Record<string, { id: string; label: string }[]> = {};
    const device_list: any[] = [];
    for (const device of response) {
      const device_type_label = (device?.deviceType?.label as string) || "";
      const device_type = device_type_label;
      device_type_set.add(device_type_label);

      const obj = {
        id: device.deviceid,
        label: trimAssetName(device.name),
      };
      device_type_to_device_id_map[device_type] ??= [];
      device_type_to_device_id_map[device_type].push(obj);
      device_list.push(obj);
    }
    setState((state: any) => ({
      ...state,
      device_list_loading: false,
      device_list,
      all_device_type_list: Array.from(device_type_set),
      device_type_to_device_id_map,
    }));
  }

  useEffect(() => {
    getConnectedDeviceListWithInfo();
  }, []);

  if (state.loading || state.device_list_loading) {
    return (
      <div style={{ width: "100%", height: "100%" }} className="flex items-center justify-center">
        <CircularProgress />
      </div>
    );
  }
  const defaultValues = {
    device_list: state.connected_devices.length === 0 ? [] : state.connected_devices,
    device_type_list: state.device_type_list.length === 0 ? [] : state.device_type_list,
  };

  return (
    <ConnectionForm
      defaultValues={defaultValues}
      {...props}
      device_list={state.device_list}
      all_device_type_list={state.all_device_type_list}
      device_type_to_device_id_map={state.device_type_to_device_id_map}
    />
  );
};

const ConnectionForm: React.FC<any> = (props) => {
  const { device, onClose, defaultValues, all_device_type_list, device_type_to_device_id_map } = props;

  const fields: FormFieldType[] = [
    {
      type: FormFieldTypesAllowed.Dropdown,
      id: "device_type_list",
      label: "Select Device Type(s)",
      required: true,
      multiple: true,
      options: all_device_type_list,
    },
    {
      type: FormFieldTypesAllowed.REACTIVE_DROPDOWN,
      id: "device_list",
      label: "Select Device(s)",
      required: true,
      multiple: true,
      reactive_key: "device_type_list",
      // options: state.device_list,
      get_options: (parent_value: string[]) => {
        const device_type_to_device_map: Record<string, { id: string; label: string }[]> = device_type_to_device_id_map;
        let final: { id: string; label: string }[] = [];
        for (const device_type_label of parent_value) {
          const value = device_type_to_device_map?.[device_type_label] ?? [];
          final = [...final, ...value];
        }
        return final;
      },
    },
  ];

  async function handleFormSubmit(values: any) {
    console.log("values", values);
    const _selected_devices_list = values.device_list;
    try {
      const params = {
        deviceId: device.deviceId,
        deviceListId: _selected_devices_list.map((device: any) => device.id).toString(),
      };
      await axios.post(APP_BASE_URL + SUB_DEVICES_PATH, null, { params });
      toast.success("Connection(s) added successfully");
      onClose();
    } catch (error) {
      console.error("SUBMIT_DEVICE_CONNECTION", error);
    }
  }

  return (
    <div className="p-2">
      <FormGenerator
        formFields={fields}
        defaultValues={defaultValues}
        handleFormSubmit={handleFormSubmit}
        // loading={state.device_list_loading}
        loading={false}
        disableSubmit={false}
        // className="store-item--form"
        className="store-item--form flex flex-col gap-2"
      />
    </div>
  );
};

export default ModalWrapper;
